// Dynamic import for FullCalendar to enable code-splitting
async function loadFullCalendar() {
    if (window.FullCalendar) return window.FullCalendar;

    const [
        { Calendar },
        classicThemePlugin,
        dayGridPlugin,
        timeGridPlugin,
        listPlugin,
        interactionPlugin,
        bootstrap5Plugin,
    ] = await Promise.all([
        import('fullcalendar'),
        import('fullcalendar/themes/classic'),
        import('fullcalendar/daygrid'),
        import('fullcalendar/timegrid'),
        import('fullcalendar/list'),
        import('fullcalendar/interaction'),
        import('@fullcalendar/bootstrap5'),
    ]);

    await Promise.all([import('fullcalendar/skeleton.css'), import('fullcalendar/themes/classic/theme.css')]);

    window.FullCalendar = {
        Calendar,
        plugins: {
            classicTheme: classicThemePlugin.default || classicThemePlugin,
            bootstrap5: bootstrap5Plugin.default || bootstrap5Plugin,
            dayGrid: dayGridPlugin.default || dayGridPlugin,
            timeGrid: timeGridPlugin.default || timeGridPlugin,
            list: listPlugin.default || listPlugin,
            interaction: interactionPlugin.default || interactionPlugin,
        },
    };

    return window.FullCalendar;
}

// Event cache to reduce API calls
const eventCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fetch paginated results with caching, dynamic headers, and single-retry
// silent token refresh on 401 (used for the room page's short-lived
// calendar page token, which can expire while a visitor is browsing).
async function fetchPaginated(apiUrl, params = {}, options = {}) {
    const { headers = {}, getHeaders = null, refreshToken = null, useCache = true } = options;

    const cacheKey = params.start || params.end ? `${apiUrl}?${new URLSearchParams(params).toString()}` : apiUrl;

    if (useCache) {
        const cached = eventCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            return cached.data;
        }
    }

    const results = [];
    let url = new window.URL(apiUrl, window.location.origin);

    Object.keys(params).forEach((k) => {
        if (params[k] !== undefined && params[k] !== null) {
            url.searchParams.set(k, params[k]);
        }
    });

    async function doFetch(fetchUrl, allowRefresh) {
        const currentHeaders = getHeaders ? getHeaders() : headers;
        const resp = await fetch(fetchUrl, {
            headers: {
                Accept: 'application/json',
                ...currentHeaders,
            },
            credentials: 'same-origin',
        });

        if (resp.status === 401 && allowRefresh && refreshToken) {
            const refreshed = await refreshToken().catch(() => null);
            if (refreshed) {
                return doFetch(fetchUrl, false);
            }
        }

        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            throw new Error(`Failed to fetch events: ${resp.status} ${text}`);
        }
        return resp;
    }

    while (url) {
        const resp = await doFetch(url.toString(), true);
        const data = await resp.json();

        if (Array.isArray(data)) {
            eventCache.set(cacheKey, { data, timestamp: Date.now() });
            return data;
        }

        if (data.results && Array.isArray(data.results)) {
            results.push(...data.results);
            url = data.next ? new window.URL(data.next) : null;
        } else {
            const values = Object.values(data);
            eventCache.set(cacheKey, { data: values, timestamp: Date.now() });
            return values;
        }
    }

    eventCache.set(cacheKey, { data: results, timestamp: Date.now() });
    return results;
}

function preprocessEvent(item) {
    return item;
}

// Event source factory with refresh support
function calendarEventSourceFactory(apiUrl, options = {}) {
    const startParam = options.startParam || 'start';
    const endParam = options.endParam || 'end';
    const dateFormat = options.dateFormat || 'iso';
    const requestHeaders = options.headers || {};
    const getHeaders = options.getHeaders || null;
    const refreshToken = options.refreshToken || null;

    let isLoading = false;
    let onLoadingChange = null;
    let onError = null;

    function formatForApi(isoString) {
        if (!isoString) return undefined;
        if (dateFormat === 'date') {
            try {
                const d = new Date(isoString);
                if (Number.isNaN(d.getTime())) return String(isoString).split('T')[0];
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            } catch (e) {
                console.error('Error formatting date:', e);
                return String(isoString).split('T')[0];
            }
        }
        try {
            const d = new Date(isoString);
            if (Number.isNaN(d.getTime())) return isoString;
            return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
        } catch (e) {
            console.error('Error formatting ISO string:', e);
            return isoString;
        }
    }

    async function fetchEvents(startStr, endStr) {
        const start = formatForApi(startStr);
        const end = formatForApi(endStr);

        const items = await fetchPaginated(
            apiUrl,
            {
                [startParam]: start,
                [endParam]: end,
            },
            {
                headers: requestHeaders,
                getHeaders,
                refreshToken,
                useCache: true,
            }
        );

        return Array.isArray(items) ? items.map(preprocessEvent) : [];
    }

    const eventSource = {
        refresh: async function (info, successCallback, failureCallback) {
            if (isLoading && onLoadingChange) {
                onLoadingChange(true);
            }

            try {
                const events = await fetchEvents(info.startStr, info.endStr);
                successCallback(events);
            } catch (err) {
                if (onError) {
                    onError(err);
                }
                if (typeof failureCallback === 'function') {
                    failureCallback(err);
                } else {
                    console.error('Error loading calendar events:', err);
                }
            } finally {
                if (onLoadingChange) {
                    onLoadingChange(false);
                }
            }
        },

        fetch: async function (info, successCallback, failureCallback) {
            return eventSource.refresh(info, successCallback, failureCallback);
        },
    };

    eventSource.setLoadingCallback = function (cb) {
        onLoadingChange = cb;
    };
    eventSource.setErrorCallback = function (cb) {
        onError = cb;
    };
    eventSource.isLoading = function () {
        return isLoading;
    };

    return eventSource;
}

// Initialize calendar with enhanced features
async function initCalendar(elOrSelector, apiUrl, options = {}) {
    const fc = await loadFullCalendar();
    const el = typeof elOrSelector === 'string' ? document.querySelector(elOrSelector) : elOrSelector;
    if (!el) throw new Error('Calendar element not found');

    const loadingEl = options.loadingElementId ? document.getElementById(options.loadingElementId) : null;

    const plugins = [
        fc.plugins.bootstrap5,
        fc.plugins.classicTheme,
        fc.plugins.dayGrid,
        fc.plugins.timeGrid,
        fc.plugins.list,
        fc.plugins.interaction,
    ].filter(Boolean);

    const initialView = options.initialView || 'timeGridWeek';
    const eventSource = calendarEventSourceFactory(apiUrl, options.eventSourceOptions || {});

    if (loadingEl) {
        eventSource.setLoadingCallback(function (isLoading) {
            loadingEl.style.display = isLoading ? 'flex' : 'none';
        });
    }

    if (options.eventError) {
        eventSource.setErrorCallback(options.eventError);
    }

    const calendar = new fc.Calendar(
        el,
        Object.assign(
            {
                plugins,
                initialView,
                headerToolbar: {
                    start: 'title',
                    end: 'today prev,next',
                },
                events: function (info, successCallback, failureCallback) {
                    eventSource.fetch(info, successCallback, failureCallback);
                },
                loading: function (isLoading) {
                    if (loadingEl) {
                        loadingEl.style.display = isLoading ? 'flex' : 'none';
                    }
                },
                eventDidMount: function (info) {
                    if (info.event.extendedProps.error) {
                        info.el.classList.add('fc-event-error');
                    }
                },
            },
            options.calendarOptions || {}
        )
    );

    calendar.render();
    return calendar;
}

// Export APIs
export { loadFullCalendar, calendarEventSourceFactory, fetchPaginated, preprocessEvent, initCalendar };

// Global exposure for template access
window.loadFullCalendar = loadFullCalendar;
window.calendarEventSourceFactory = calendarEventSourceFactory;
window.fetchPaginated = fetchPaginated;
window.preprocessEvent = preprocessEvent;
window.initCalendar = initCalendar;
